/* import __COLOCATED_TEMPLATE__ from './suggestions.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ArticleContent from 'embercom/models/articles/article-content';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import {
  ContentAction,
  type LocalizedKnowledgeContent,
} from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type Model from '@ember-data/model';
import { EntityType } from 'embercom/models/data/entity-types';
import type Store from '@ember-data/store';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import type RouterService from '@ember/routing/router-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { captureException } from 'embercom/lib/sentry';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type IntlService from 'ember-intl/services/intl';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import {
  type OptimizeSuggestionItem,
  OptimizeSuggestionType,
} from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/constants';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { isRecentAiInsightsPurchase } from '../lib/is-recent-ai-insights-purchase';

interface Signature {
  Args: {
    activeContentId?: string;
    activeContentType?: string;
  };
}

const NEXUS_TOPIC = 'suggestions-generation-finished';
const NEXUS_EVENT_NAME = 'SuggestionsGenerationFinished';

export default class Suggestions extends Component<Signature> {
  @service declare appService: AppService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare navbarCollapsingService: $TSFixMe;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare suggestionService: SuggestionService;
  @service declare realTimeEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;

  @tracked private isHandlingTransition = false;
  @tracked private segmentationSettingsSideDrawerOpen = false;
  @tracked private showStandaloneSideDrawer = false;

  @tracked showConversationPanel = false;
  @tracked selectedConversation: ConversationExploreConversation | undefined = undefined;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.router.on('routeWillChange', this.warnIfUnsavedChanges);
    this.realTimeEventService.subscribeTopics([NEXUS_TOPIC]);
    this.realTimeEventService.on(NEXUS_EVENT_NAME, this, 'handleSuggestionsGenerationFinished');
    this.setupResizeListener();
    this.checkScreenSize();
  }

  willDestroy() {
    super.willDestroy();
    this.router.off('routeWillChange', this.warnIfUnsavedChanges);
    this.realTimeEventService.unsubscribeTopics([NEXUS_TOPIC]);
    this.realTimeEventService.off(NEXUS_EVENT_NAME, this, 'handleSuggestionsGenerationFinished');
    this.removeResizeListener();
  }

  private setupResizeListener() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  private removeResizeListener() {
    window.removeEventListener('resize', this.handleResize);
  }

  private handleResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    let narrowScreenWidthPixels = 1_440;
    let isNarrowScreen = window.innerWidth < narrowScreenWidthPixels;
    if (isNarrowScreen && !this.navbarCollapsingService.collapsed) {
      this.navbarCollapsingService.collapsed = true;
    }
  }

  @action
  toggleSubmenu() {
    this.navbarCollapsingService.collapsed = !this.navbarCollapsingService.collapsed;
  }

  get isDataAvailable(): boolean {
    return !this.optimizeSuggestionsService.isEmptyState;
  }

  get shouldShowPostPurchaseEmptyState(): boolean {
    return isRecentAiInsightsPurchase(this.appService.app.products) && !this.isDataAvailable;
  }

  get suggestions() {
    return this.optimizeSuggestionsService.suggestions;
  }

  get fullSuggestions() {
    return this.optimizeSuggestionsService.fullSuggestions;
  }

  get app() {
    return this.appService.app;
  }

  get isSubmenuCollapsed() {
    return this.navbarCollapsingService.collapsed;
  }

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  get isZendeskOrSalesforceStandalone(): boolean {
    return (
      this.appService.app.isStandaloneApp &&
      (this.appService.app.hasStandalonePlatform('zendesk') ||
        this.appService.app.hasStandalonePlatform('salesforce'))
    );
  }

  get isHistoricalSyncingEnabled(): boolean {
    return this.appService.mainStandalonePlatform?.historicalSyncingEnabled ?? false;
  }

  get finIsLive(): boolean {
    return this.appService.mainStandalonePlatform?.finIsLive ?? false;
  }

  get hasChatChannels(): boolean {
    if (!this.isZendeskOrSalesforceStandalone) {
      return false;
    }
    // Check for Intercom Messenger
    let hasMessenger =
      this.finStandaloneService.intercomMessengerConfig?.isMessengerEnabled ?? false;
    // Check for Zendesk Sunshine
    if (this.appService.app.hasStandalonePlatform('zendesk')) {
      let hasSunshine =
        this.finStandaloneService.zendeskConfig?.sunshineIntegration
          ?.isEnabledForSunshineMessenger ?? false;
      return hasMessenger || hasSunshine;
    }
    return hasMessenger;
  }

  // Scenario 1: Tickets-only without sync - show empty state (only when Fin is deployed)
  get shouldShowTicketSyncEmptyState(): boolean {
    return (
      this.isZendeskOrSalesforceStandalone &&
      this.finIsLive &&
      !this.isHistoricalSyncingEnabled &&
      !this.hasChatChannels
    );
  }

  // Scenario 2: Tickets + chat without sync - show warning banner
  get shouldShowTicketSyncWarningBanner(): boolean {
    return (
      this.isZendeskOrSalesforceStandalone &&
      !this.isHistoricalSyncingEnabled &&
      this.hasChatChannels
    );
  }

  get standalonePlatformName(): 'zendesk' | 'salesforce' {
    if (this.appService.app.hasStandalonePlatform('zendesk')) {
      return 'zendesk';
    }
    return 'salesforce';
  }

  get optimizeSurveyId(): number {
    return this.appService.app.canUseStandalone ? 56982439 : 56613326;
  }

  get hasSuggestions() {
    return this.suggestions.length > 0;
  }

  get suggestionCount() {
    return this.suggestions.length;
  }

  get activeSuggestion(): OptimizeSuggestionItem | undefined {
    return this.optimizeSuggestionsService.findSuggestionByEntityId(
      this.args.activeContentId?.toString() || '',
    );
  }

  get activeContent() {
    return this.knowledgeHubEditorService.activeContentModel;
  }

  get loadingActiveContent(): boolean {
    return this.activeContentLoader.isLoading;
  }

  get shouldShowSideBySideReview() {
    return this.args.activeContentType === objectNames[EntityType.ContentSectionRelationship];
  }

  get contentSectionRelationship() {
    return this.knowledgeHubEditorService.reviewableActiveContent as ContentSectionRelationship;
  }

  private activeContentLoader = DeduplicatedAsyncData(
    this,
    () => [this.args.activeContentId, this.args.activeContentType],
    async (activeContentId, activeContentType) => {
      if (!activeContentId || !activeContentType) {
        return null;
      }

      if (this.isHandlingTransition) {
        return this.activeContent;
      }

      if (activeContentType === objectNames[EntityType.ContentSectionRelationship]) {
        return await this.fetchContentSectionRelationshipContents(Number(activeContentId));
      }

      return await this.fetchReviewModeActiveContent(Number(activeContentId), activeContentType);
    },
  );

  private async fetchContentSectionRelationshipContents(contentSectionRelationshipId: number) {
    let contentSectionRelationship = await this.store.findRecord(
      'content-service/content-section-relationship',
      contentSectionRelationshipId,
    );

    this.knowledgeHubEditorService.registerActiveContent(contentSectionRelationship);
    let promises = [
      contentSectionRelationship.sourceSection.loadRelatedContent(ContentAction.EDIT),
      contentSectionRelationship.targetSection.loadRelatedContent(ContentAction.EDIT),
    ];
    await Promise.all(promises);
    return contentSectionRelationship;
  }

  private async fetchReviewModeActiveContent(
    activeContentId: number,
    activeContentType: string,
  ): Promise<(LocalizedKnowledgeContent & Model) | null> {
    let activeContent: (LocalizedKnowledgeContent & Model) | null = null;
    try {
      activeContent = await this.knowledgeHubService.findContent({
        contentId: activeContentId,
        contentTypeName: activeContentType,
      });

      if (activeContent) {
        await this.fetchContentReviewRequest(activeContent);
      }
    } catch (error) {
      return null;
    }

    if (!activeContent) {
      return null;
    }

    await activeContent.loadRelatedContent(ContentAction.EDIT);
    this.knowledgeHubEditorService.registerActiveContent(activeContent);

    return activeContent;
  }

  private async fetchContentReviewRequest(activeContent: LocalizedKnowledgeContent & Model) {
    return (activeContent as ArticleContent | ContentSnippet).contentReviewRequest;
  }

  get fromConDupeSuggestion() {
    return this.knowledgeHubDrawerEditorService.fromConDupeSuggestion;
  }

  @action
  async closeDrawer() {
    if (this.fromConDupeSuggestion) {
      await this.reloadRelatedContent();
      this.knowledgeHubDrawerEditorService.openReviewDrawer({
        activeContentId: Number(this.fromConDupeSuggestion),
        activeContentType: objectNames[EntityType.ContentSectionRelationship],
        queryParams: {
          fromConDupeSuggestion: null,
        },
      });
    }
  }

  @action
  async reloadRelatedContent() {
    let contentSectionRelationship = await this.store.findRecord(
      'content-service/content-section-relationship',
      Number(this.fromConDupeSuggestion),
    );

    if (!contentSectionRelationship) {
      return;
    }

    let promises = [
      contentSectionRelationship.sourceSection.loadRelatedContent(ContentAction.EDIT, true),
      contentSectionRelationship.targetSection.loadRelatedContent(ContentAction.EDIT, true),
    ];
    await Promise.all(promises);
  }

  @action
  async warnIfUnsavedChanges(transition: any) {
    let previousContentId = this.knowledgeHubEditorService.activeContentModel?.entityId;
    let previousEntityType = this.knowledgeHubEditorService.activeContentModel?.entityType;
    let hasRouteChanged = transition.from?.name !== transition.to?.name;

    if (hasRouteChanged && this.suggestionService.hasDirtyContent) {
      transition.abort();
      this.isHandlingTransition = true;
      this.suggestionService.isChangingContent = true;

      try {
        let confirmationResult = await this.knowledgeHubDrawerEditorService.confirmClose();

        if (confirmationResult === true) {
          await this.knowledgeHubDrawerEditorService.performContentAction('save');
          this.isHandlingTransition = false;
          if (this.suggestionService.allReviewRequestsAreActioned) {
            await this.onAllReviewRequestsActioned(previousContentId, previousEntityType);
          }
          transition.retry();
        } else if (confirmationResult.canceled) {
          await this.knowledgeHubDrawerEditorService.performContentAction('cancel');
          this.isHandlingTransition = false;
          if (this.allReviewRequestsRejected()) {
            await this.onAllReviewRequestsActioned(previousContentId, previousEntityType);
          }
          transition.retry();
        } else {
          this.isHandlingTransition = false;
        }
      } catch (error) {
        captureException(error);
        this.isHandlingTransition = false;
      } finally {
        this.suggestionService.isChangingContent = false;
      }
    }
  }

  @action
  showRelatedConversation(conversation: ConversationExploreConversation): void {
    this.selectedConversation = conversation;
    this.showConversationPanel = true;
  }

  @action
  toggleConversationPanel(): void {
    this.showConversationPanel = !this.showConversationPanel;
  }

  @action
  openStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = true;
  }

  @action
  closeStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = false;
  }

  get suggestedConversationIds() {
    return this.activeSuggestion?.conversationIds ?? [];
  }

  get impactRanking() {
    return this.activeSuggestion?.impactRanking;
  }

  get conversationCount() {
    return this.activeSuggestion?.conversationCount;
  }

  get shouldRenderSuggestion() {
    return this.hasSuggestions && Boolean(this.activeSuggestion);
  }

  get isContentGapSuggestion() {
    return this.activeSuggestion?.suggestionType === OptimizeSuggestionType.ContentGap;
  }

  get shouldShowSideBySide() {
    return (
      this.shouldRenderSuggestion && this.isContentGapSuggestion && this.shouldShowSideBySideReview
    );
  }

  get shouldShowContentReview() {
    return (
      // Content gap without side-by-side
      (this.shouldRenderSuggestion &&
        this.isContentGapSuggestion &&
        !this.shouldShowSideBySideReview) ||
      // Direct content navigation
      (!this.activeSuggestion && Boolean(this.activeContent))
    );
  }

  get shouldShowTaskReview() {
    return this.shouldRenderSuggestion && !this.isContentGapSuggestion;
  }

  get taskSuggestionForReview(): OptimizeSuggestionItem {
    // This getter is only accessed when shouldShowTaskReview is true,
    // which guarantees activeSuggestion exists via shouldRenderSuggestion
    return this.activeSuggestion!;
  }

  get showBackButton() {
    return Boolean(this.fromConDupeSuggestion);
  }

  private allReviewRequestsRejected() {
    return this.suggestionService.reviewRequests.every((reviewRequest) => {
      return reviewRequest.get('status') === Status.Rejected;
    });
  }

  private async onAllReviewRequestsActioned(
    previousContentId: number,
    previousEntityType: EntityType,
  ) {
    await timeout(ENV.APP._1000MS);

    this.suggestionService.contentSuggestionsService.removeSuggestionFromList({
      entity_id: previousContentId,
      entity_type: previousEntityType,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions': typeof Suggestions;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions': typeof Suggestions;
  }
}
